import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SelectOption } from '@/types/select';
import { formatCardNumber } from '@/utils';
import { TUserCardInfo } from '@xhype/card-app-sdk';
import { useGetCards } from '@package/smart-wallet/hooks';

import { CardContext } from './context';

interface SelectedCard {
  id: number;
  name: string;
  value: string;
}

export const CardProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoading, cardsData, error, refetch } = useGetCards();
  const [selectedCard, setSelectedCard] = useState<SelectedCard | null>(null);

  const selectedCardInfo = useMemo<TUserCardInfo | null>(() => {
    if (!selectedCard || !cardsData) return null;
    return cardsData.find((card) => card.id === selectedCard.id) || null;
  }, [selectedCard, cardsData]);

  // Update selected card when cardsData changes and no card is selected
  useEffect(() => {
    if (cardsData && cardsData.length > 0 && !selectedCard) {
      const firstCard = cardsData[0];
      // console.log(firstCard);
      setSelectedCard({
        id: firstCard.id,
        name: formatCardLabel(firstCard),
        value: firstCard.id.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsData, selectedCard]);

  // Helper function: Format card option label
  const formatCardLabel = useCallback((card: TUserCardInfo) => {
    const alias = card.alias || '';
    const cardNumber = card.cardNumber || '';
    // Show only alias if it's the same as cardNumber, otherwise show both
    return alias.includes(cardNumber)
      ? alias
      : `${alias} - ${formatCardNumber(cardNumber)}`;
  }, []);

  // Update selected card name when the card data changes (e.g., alias updated)
  useEffect(() => {
    if (selectedCard && cardsData && cardsData.length > 0) {
      const updatedCard = cardsData.find((card) => card.id === selectedCard.id);
      if (updatedCard) {
        const newName = formatCardLabel(updatedCard);
        if (newName !== selectedCard.name) {
          setSelectedCard({
            id: updatedCard.id,
            name: newName,
            value: updatedCard.id.toString(),
          });
        }
      }
    }
  }, [cardsData, selectedCard, formatCardLabel]);

  // Helper function: Map cards to select options
  const cardOptions = useMemo<SelectOption[]>(() => {
    if (!cardsData || cardsData.length === 0) return [];
    return cardsData.map((card: any) => ({
      id: card.id,
      name: formatCardLabel(card),
      value: card.id.toString(),
    }));
  }, [cardsData, formatCardLabel]);

  const value = {
    selectedCard,
    selectedCardInfo,
    setSelectedCard,
    cardsData,
    cardOptions,
    isLoading,
    error,
    refetch,
  };

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>;
};
